'use client';

import React, { Suspense, lazy, useEffect, useState } from 'react';

import type { Engine, IOptions, RecursivePartial } from '@tsparticles/engine';
import { initParticlesEngine } from '@tsparticles/react';

import { useMediaQuery } from '@/lib/hooks';

const LazyParticles = lazy(() => import('@tsparticles/react'));

async function lazyLoadFull(engine: Engine, refresh?: boolean): Promise<void> {
    const { loadFull } = await import('tsparticles');
    return loadFull(engine, refresh);
}

export const ParticlesBg = () => {
    const [init, setInit] = useState(false);

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await lazyLoadFull(engine);
        }).then(() => {
            setInit(true);
        });
    }, []);

    const isDesktop = useMediaQuery('(min-width: 1400px)');

    const options: RecursivePartial<IOptions> = {
        background: {
            opacity: 0,
            repeat: 'repeat',
        },
        fullScreen: {
            enable: false,
            zIndex: 0,
        },
        fpsLimit: 144,
        interactivity: {
            events: {
                onClick: {
                    enable: isDesktop,
                    mode: 'push',
                },
                onHover: {
                    enable: isDesktop,
                    mode: 'grab',
                    parallax: {
                        enable: true,
                        force: 60,
                        smooth: 10,
                    },
                },
            },
            modes: {
                push: {
                    quantity: 5,
                },
                attract: {
                    distance: 200,
                    duration: 0.4,
                    easing: 'ease-out-quad',
                    factor: 1,
                    maxSpeed: 50,
                    speed: 1,
                },
                bounce: {
                    distance: 200,
                },
                grab: {
                    distance: 400,
                    links: {
                        blink: false,
                        consent: false,
                        opacity: 1,
                    },
                },
                connect: {
                    distance: 80,
                    links: {
                        opacity: 0.5,
                    },
                    radius: 60,
                },
            },
        },
        particles: {
            color: {
                value: '#83b1df',
            },
            links: {
                color: '#83b1df',
                distance: 250,
                enable: true,
                opacity: 0.5,
                width: 1,
            },
            move: {
                direction: 'none',
                enable: true,
                outModes: {
                    default: 'out',
                },
                random: false,
                speed: 2.6,
                straight: false,
            },
            number: {
                density: {
                    enable: true,
                    width: 1920,
                    height: 1080,
                },
                value: 90,
                limit: {
                    mode: 'delete',
                    value: 90,
                },
            },
            opacity: {
                value: 0.8,
            },
            shape: {
                type: 'triangle',
                close: true,
                fill: true,
            },
            size: {
                value: { min: 0.5, max: 6.5 },
            },
        },
        detectRetina: true,
        pauseOnBlur: true,
        pauseOnOutsideViewport: true,
        style: {
            position: 'absolute',
            width: '100%',
            height: '100%',
        },
    };

    return (
        <>
            {init && (
                <Suspense fallback={<div />}>
                    <LazyParticles id="tsparticles" options={options} />
                </Suspense>
            )}
        </>
    );
};
